import React, { FC, useEffect, useState } from 'react';
import dimeSealLogo from '../../assets/DiMe-Seal-2026-RBG.png';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Fade,
  LinearProgress,
  Paper,
} from '@mui/material';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import LockIcon from '@mui/icons-material/Lock';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { BRAND, BRAND_DARK, BRAND_DARKER, BG } from './healthWalletConstants';
//import { useOnboarding } from './OnboardingContext';
import { getCurrentOnboardingStep } from './onboardingUtils';

interface OnboardingLandingProps {
  onNewUser: () => void;
}

const FEATURES = [
  {
    icon: <HealthAndSafetyIcon sx={{ fontSize: 40, color: BRAND_DARKER }} />,
    title: 'Your Records, One Place',
    description: 'Access medical records from every provider across nationwide health networks through QHIN-certified exchanges.',
  },
  {
    icon: <LockIcon sx={{ fontSize: 40, color: BRAND_DARKER }} />,
    title: 'You Control Your Data',
    description: 'Your consent drives everything. We never access records without your explicit, digitally-signed authorization.',
  },
  {
    icon: <SecurityIcon sx={{ fontSize: 40, color: BRAND_DARKER }} />,
    title: 'Identity Verified by CLEAR',
    description: 'Industry-leading identity verification ensures only you can access your health information.',
  },
  {
    icon: <VolunteerActivismIcon sx={{ fontSize: 40, color: BRAND_DARKER }} />,
    title: 'Free for All Patients',
    description: 'Your Health Wallet is completely free — no subscriptions, no hidden fees. Every patient deserves easy access to their own health records.',
  },
  {
    icon: <SpeedIcon sx={{ fontSize: 40, color: BRAND_DARKER }} />,
    title: 'AI Health Assistant',
    description: 'Ask Polly, your AI assistant, to explain results, spot trends, and help you prepare for appointments.',
  },
  {
    icon: <MonitorHeartIcon sx={{ fontSize: 40, color: BRAND_DARKER }} />,
    title: 'Track Trends Over Time',
    description: 'Visualize lab results, vitals, and health markers with interactive charts and timeline views.',
  },
];

export const OnboardingLanding: FC<OnboardingLandingProps> = ({ onNewUser }) => {
  // 🆕 Use onboarding context for both verification and loading state
  // const { verification, isLoading, isSuccess } = useOnboarding();
  const currentStep = getCurrentOnboardingStep();
  console.log('OnboardingLanding: currentStep', currentStep);
  
  // 🆕 State for showing buttons (default to null)
  const [showButtons, setShowButtons] = useState<boolean>(true);
  
  // // 🆕 Update showButtons state based on loading and step
  // useEffect(() => {
  //   setShowButtons(isSuccess && currentStep === 'landing');
  // }, [isLoading, currentStep]);
  
  // // 🆕 Don't show landing screen if returning from Clear redirect
  // if (verification?.isReturningFromClear) {
  //   return (
  //     <Box sx={{ 
  //       display: 'flex', 
  //       justifyContent: 'center', 
  //       alignItems: 'center', 
  //       minHeight: '100vh',
  //       bgcolor: BG 
  //     }}>
  //       <CircularProgress sx={{ color: BRAND_DARKER }} />
  //     </Box>
  //   );
  // }

  // 🆕 Hide buttons by default and while loading
  //const isDisabled = isLoading;

  return (
    <Box sx={{ bgcolor: BG, minHeight: '100svh', width: '100%', display: 'flex', flexDirection: 'column', mt: { xs: '-56px', sm: '-64px' } }}>
      {/* Hero Section */}
      <Box
        component="header"
        sx={{
          pt: { xs: '104px', sm: '144px' },
          pb: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Fade in timeout={800}>
            <Box sx={{ textAlign: 'center' }}>
              <Box
                component="a"
                href="https://seal.dimesociety.org"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: 'inline-block', mb: 3, textDecoration: 'none', opacity: 0.95, '&:hover': { opacity: 1 }, transition: 'opacity 0.2s' }}
              >
                <Box
                  component="img"
                  src={dimeSealLogo}
                  alt="DiMe Society Seal"
                  sx={{ height: 120, width: 120, objectFit: 'contain', filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.25))' }}
                />
              </Box>

              <Typography
                variant="h3"
                component="h1"
                sx={{
                  color: '#1c1c1e',
                  fontWeight: 800,
                  mb: 2,
                  fontSize: { xs: '2rem', md: '3rem' },
                  lineHeight: 1.2,
                }}
              >
                Your Complete Health Story,
                <br />
                In Your Hands
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  color: '#6b6b70',
                  fontWeight: 400,
                  mb: 5,
                  maxWidth: 600,
                  mx: 'auto',
                  lineHeight: 1.6,
                  fontSize: { xs: '1rem', md: '1.15rem' },
                }}
              >
                Securely gather your medical records from providers nationwide.
                Powered by FHIR interoperability and verified with CLEAR identity protection.
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center' }}>
                {!showButtons ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <LinearProgress 
                      sx={{ 
                        width: 140, 
                        height: 2, 
                        borderRadius: 1,
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 1,
                        }
                      }} 
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.8 }}>
                      Loading...
                    </Typography>
                  </Box>
                ) : (
                  <Button
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForwardIcon />}
                    onClick={onNewUser}
                    sx={{
                      bgcolor: BRAND_DARKER,
                      color: 'white',
                      fontWeight: 700,
                      px: 4,
                      py: 1.5,
                      borderRadius: 50,
                      fontSize: '1rem',
                      textTransform: 'none',
                      boxShadow: '0 4px 20px rgba(108,76,224,0.3)',
                      '&:hover': { bgcolor: '#5a3fc0', boxShadow: '0 6px 24px rgba(108,76,224,0.4)' },
                    }}
                  >
                    Get Started
                  </Button>
                )}

              </Box>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Features Grid */}
      <Container component="main" maxWidth="md" sx={{ mt: -4, position: 'relative', zIndex: 2, pb: 8 }}>
        <Paper
          elevation={3}
          sx={{
            borderRadius: 4,
            p: { xs: 3, md: 5 },
            bgcolor: 'white',
            boxShadow: '0 8px 40px rgba(0,0,0,0.08)',
          }}
        >
          <Typography variant="h5" fontWeight={700} sx={{ mb: 1, color: '#1c1c1e', textAlign: 'center' }}>
            How It Works
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, textAlign: 'center', maxWidth: 500, mx: 'auto' }}>
            A simple, secure process to unlock your complete medical history.
          </Typography>

          {/* DiME Certification Badge */}
          <Box
            component="a"
            href="https://seal.dimesociety.org"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'flex', alignItems: 'center', gap: 2,
              mb: 4, p: 2, borderRadius: 3,
              border: '1px solid #d7d3f0', bgcolor: BG,
              textDecoration: 'none',
              transition: 'box-shadow 0.2s',
              '&:hover': { boxShadow: '0 4px 16px rgba(108,76,224,0.1)' },
            }}
          >
            <Box
              component="img"
              src={dimeSealLogo}
              alt="DiMe Society Seal"
              sx={{ height: 100, width: 100, objectFit: 'contain', flexShrink: 0 }}
            />
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#1c1c1e' }}>
                Certified by DiME
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                Verified by the Digital Medicine Society for security, privacy, usability, clinical evidence, and equity standards.
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={3}>
            {FEATURES.map((feature, i) => (
              <Grid size={{ xs: 12, sm: 6 }} key={i}>
                <Fade in timeout={600 + i * 150}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      bgcolor: BG,
                      height: '100%',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 16px rgba(108,76,224,0.1)' },
                    }}
                  >
                    <Box sx={{ mb: 1.5 }}>{feature.icon}</Box>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5, color: '#1c1c1e' }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      {feature.description}
                    </Typography>
                  </Paper>
                </Fade>
              </Grid>
            ))}
          </Grid>

          {/* Bottom CTA */}
          <Box sx={{ textAlign: 'center', mt: 5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Setup takes about 3 minutes. Your data stays private and secure.
            </Typography>
            {!showButtons ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <LinearProgress 
                  sx={{ 
                    width: 200, 
                    height: 3, 
                    borderRadius: 1.5,
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 1.5,
                      bgcolor: BRAND_DARKER,
                    }
                  }} 
                />
                <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.7 }}>
                  Preparing your health wallet...
                </Typography>
              </Box>
            ) : (
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                onClick={onNewUser}
                sx={{
                  bgcolor: BRAND_DARKER,
                  fontWeight: 700,
                  px: 5,
                  py: 1.5,
                  borderRadius: 50,
                  fontSize: '1rem',
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#5a3fc0' },
                }}
              >
                Start Your Health Wallet
              </Button>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};
